import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, MapPin, Users, ChevronDown, ChevronUp, XCircle, HardHat, Shield, Store, CalendarRange } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import RangeGenerator from '../../components/client/RangeGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_META = {
  labour: { icon: HardHat, cls: 'bg-amber-500/10 text-amber-600 border-amber-300' },
  security: { icon: Shield, cls: 'bg-blue-500/10 text-blue-600 border-blue-300' },
  retail: { icon: Store, cls: 'bg-purple-500/10 text-purple-600 border-purple-300' },
};

const STATUS_CLS = {
  active: 'bg-green-500/10 text-green-600 border-green-300',
  cancelled: 'bg-red-500/10 text-red-600 border-red-300',
  completed: 'bg-zinc-200 text-zinc-600 border-zinc-300',
};

export default function ClientRequestsPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [cancelTarget, setCancelTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [addTarget, setAddTarget] = useState(null);
  const [addPositions, setAddPositions] = useState([]);
  const [adding, setAdding] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/staffing-requests`);
      setRequests(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const openAddStaff = async (r) => {
    setAddTarget(r);
    try {
      const res = await axios.get(`${API}/staffing-positions?category=${r.category}`);
      setAddPositions(res.data || []);
    } catch {
      setAddPositions([]);
    }
  };

  const submitAddStaff = async (rows) => {
    if (!addTarget) return;
    setAdding(true);
    try {
      const res = await axios.post(`${API}/client/staffing-requests/${addTarget.id}/days`, { days: rows });
      toast.success(`${res.data.added_rows} row(s) added — ${res.data.added_staff} extra staff requested. Our team and employees have been notified.`);
      setAddTarget(null);
      await fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add staff');
    } finally {
      setAdding(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/staffing-requests/${cancelTarget.id}/cancel`);
      toast.success('Request cancelled');
      setCancelTarget(null);
      await fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ClientLayout>
      <div className="space-y-4" data-testid="client-requests-page">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-zinc-900 tracking-tight">Staffing Requests</h1>
            <p className="text-sm text-zinc-500 mt-0.5">All your requests and their status.</p>
          </div>
          <Button
            onClick={() => navigate('/client/requests/new')}
            data-testid="new-request-btn"
            className="bg-[#14181f] text-white hover:bg-[#232a35] rounded-md uppercase tracking-[0.12em] text-xs font-semibold shrink-0"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New Request
          </Button>
        </div>

        {loading ? (
          <p className="py-12 text-center text-sm text-zinc-500">Loading…</p>
        ) : requests.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-500" data-testid="requests-empty">
            No staffing requests yet. Click <strong>New Request</strong> to create your first one.
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => {
              const Meta = CAT_META[r.category] || CAT_META.labour;
              const isOpen = !!expanded[r.id];
              return (
                <Card key={r.id} className="bg-white border-zinc-200 rounded-lg shadow-sm" data-testid={`request-${r.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 rounded-full bg-[#14181f] flex items-center justify-center shrink-0">
                        <Meta.icon className="w-4 h-4 text-primary" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="text-sm sm:text-base font-bold text-zinc-900">{r.site_name}</p>
                          <Badge className={`text-[10px] uppercase border ${Meta.cls}`}>{r.category}</Badge>
                          <Badge className={`text-[10px] uppercase border ${STATUS_CLS[r.status] || ''}`} data-testid={`request-status-${r.id}`}>{r.status}</Badge>
                        </div>
                        <p className="mt-0.5 text-xs text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{r.address}, {r.postcode}</span>
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{r.total_staff_requested} staff · {r.days?.length} day-row(s)</span>
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}
                        data-testid={`toggle-request-${r.id}`}
                        className="text-zinc-400 hover:text-zinc-900 shrink-0"
                      >
                        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>

                    {isOpen && (
                      <div className="mt-3 border-t border-zinc-100 pt-3 space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-zinc-600">
                          <span><strong>Contact:</strong> {r.contact_person} — {r.contact_number}</span>
                          {r.purchase_order && <span><strong>PO:</strong> {r.purchase_order}</span>}
                          {r.reference_number && <span><strong>Ref:</strong> {r.reference_number}</span>}
                          {r.additional_info && <span className="sm:col-span-2"><strong>Info:</strong> {r.additional_info}</span>}
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-zinc-400 uppercase tracking-wider">
                                <th className="py-1 pr-3">Date</th>
                                <th className="py-1 pr-3">Time</th>
                                <th className="py-1 pr-3">Position</th>
                                <th className="py-1 pr-3">Staff</th>
                                <th className="py-1">Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(r.days || []).map((d, i) => (
                                <tr key={i} className="border-t border-zinc-100 text-zinc-700">
                                  <td className="py-1.5 pr-3 whitespace-nowrap">{(() => { try { return format(parseISO(d.date), 'EEE dd MMM yyyy'); } catch { return d.date; } })()}</td>
                                  <td className="py-1.5 pr-3 whitespace-nowrap">{d.start_time}–{d.end_time}</td>
                                  <td className="py-1.5 pr-3">{d.position}</td>
                                  <td className="py-1.5 pr-3">{d.staff_required}</td>
                                  <td className="py-1.5 text-zinc-500">{d.notes || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {r.status === 'active' && (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openAddStaff(r)}
                              data-testid={`add-staff-request-${r.id}`}
                              className="border-zinc-300 text-zinc-700 hover:bg-zinc-50 rounded-md text-xs"
                            >
                              <CalendarRange className="w-3.5 h-3.5 mr-1" /> Add More Staff
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setCancelTarget(r)}
                              data-testid={`cancel-request-${r.id}`}
                              className="border-red-300 text-red-500 hover:bg-red-50 rounded-md text-xs"
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel Request
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!addTarget} onOpenChange={(o) => { if (!o) setAddTarget(null); }}>
        <DialogContent className="bg-white border-zinc-200 rounded-lg max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="add-staff-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-900">Add More Staff — {addTarget?.site_name}</DialogTitle>
            <DialogDescription>
              Pick the period, times and one or more positions with the staff number per day. New jobs are created instantly and our team is notified.
            </DialogDescription>
          </DialogHeader>
          {addTarget && (
            <RangeGenerator
              positions={addPositions}
              busy={adding}
              generateLabel="Add Staff"
              onGenerate={submitAddStaff}
              onCancel={() => setAddTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) setCancelTarget(null); }}>
        <AlertDialogContent className="bg-white border-zinc-200 rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this staffing request?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget ? <><strong>{cancelTarget.site_name}</strong> — {cancelTarget.total_staff_requested} staff. Our team will be notified.</> : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting} className="rounded-md">Keep request</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} disabled={submitting} className="bg-red-500 hover:bg-red-600 text-white rounded-md" data-testid="confirm-cancel-request-btn">
              {submitting ? 'Cancelling…' : 'Yes, cancel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClientLayout>
  );
}
